import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Collapse,
  Checkbox,
  Tooltip,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export interface UrgencyCount {
  critical: number;
  urgent: number;
  soon: number;
}

export interface ActionTypeGroupProps {
  title: string;
  count: number;
  subtitle?: string;
  /** Urgency breakdown - shows colored chips instead of text subtitle */
  urgencyCounts?: UrgencyCount;
  tooltip?: string;
  icon?: React.ReactNode;
  color?: string;
  defaultExpanded?: boolean;
  forceExpanded?: boolean;
  showSelectAll?: boolean;
  allSelected?: boolean;
  onSelectAll?: () => void;
  children: React.ReactNode;
}

/**
 * Collapsible group component for organizing inbox items by action type
 */
export function ActionTypeGroup({
  title,
  count,
  subtitle,
  urgencyCounts,
  tooltip,
  icon,
  color = '#6b7280',
  defaultExpanded = false,
  forceExpanded = false,
  showSelectAll = false,
  allSelected = false,
  onSelectAll,
  children,
}: ActionTypeGroupProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Auto-expand if forceExpanded and has items
  useEffect(() => {
    if (forceExpanded && count > 0) {
      setExpanded(true);
    }
  }, [forceExpanded, count]);

  return (
    <Box data-testid={`action-group-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Group Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 1,
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'background-color 80ms ease-out',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Select All checkbox (if enabled) */}
        {showSelectAll && onSelectAll && count > 0 ? (
          <Checkbox
            checked={allSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectAll();
            }}
            size="small"
            sx={{ p: 0, mr: 0.5 }}
          />
        ) : (
          <IconButton size="small" sx={{ p: 0 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}

        {/* Icon */}
        {icon && (
          <Box sx={{ color, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}

        {/* Title */}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 1,
            color: 'text.secondary',
          }}
        >
          {title}
        </Typography>

        {/* Info Tooltip */}
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <HelpOutlineIcon
              sx={{
                fontSize: 14,
                color: 'text.disabled',
                cursor: 'help',
                '&:hover': { color: 'text.secondary' },
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        )}

        {/* Count Badge */}
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: count > 0 ? color : 'text.disabled',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </Typography>

        {/* Urgency chips - visual breakdown of critical/urgent items */}
        {urgencyCounts && (urgencyCounts.critical > 0 || urgencyCounts.urgent > 0) && (
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            {urgencyCounts.critical > 0 && (
              <Chip
                label={`${urgencyCounts.critical} ${t('screens.operationsInbox.actionTypeGroup.urgencyLabels.now')}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: '#dc2626',
                  color: 'white',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
            {urgencyCounts.urgent > 0 && (
              <Chip
                label={`${urgencyCounts.urgent} ${t('screens.operationsInbox.actionTypeGroup.urgencyLabels.today')}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: '#ea580c',
                  color: 'white',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>
        )}

        {/* Fallback text subtitle (if no urgency counts provided) */}
        {subtitle && !urgencyCounts && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              ml: 1,
              fontSize: 11,
            }}
          >
            {subtitle}
          </Typography>
        )}

        <Box flex={1} />
      </Box>

      {/* Group Content */}
      <Collapse in={expanded} timeout={200} easing="ease-out">
        <Stack spacing={1} sx={{ mt: 1.5, mb: 3 }}>
          {children}
        </Stack>
      </Collapse>

      {/* Show placeholder when collapsed and has items */}
      {!expanded && count > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pl: 5, display: 'block', mb: 2 }}
        >
          {t('screens.operationsInbox.actionTypeGroup.itemCount', { count })}
        </Typography>
      )}

      {/* Show empty state when expanded but no items */}
      {expanded && count === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ pl: 5, pb: 2, fontStyle: 'italic' }}
        >
          {t('screens.operationsInbox.actionTypeGroup.noItems')}
        </Typography>
      )}
    </Box>
  );
}
